import { useEffect } from 'react';

/**
 * Forwards vertical scroll (wheel + touch) from a source element to a target element.
 * Used so that scrolling over the sticky ProductCardZone drives the main content area.
 * Horizontal gestures are left untouched so the existing sync keeps working.
 */
export const useScrollForwarding = (sourceRef, targetRef, isEnabled = true) => {
    useEffect(() => {
        if (!isEnabled || !sourceRef.current || !targetRef.current) {
            return undefined;
        }

        const source = sourceRef.current;

        // Accumulate deltas and flush once per animation frame to avoid
        // repeated synchronous scrollTop reads/writes causing layout jank.
        let pendingDelta = 0;
        let rafId = null;

        const flushDelta = () => {
            if (targetRef.current) {
                targetRef.current.scrollTop += pendingDelta;
            }

            pendingDelta = 0;
            rafId = null;
        };

        const scheduleDelta = delta => {
            pendingDelta += delta;

            if (rafId === null) {
                rafId = requestAnimationFrame(flushDelta);
            }
        };

        // Normalize deltaY to pixels: deltaMode 1 = lines (~40px), 2 = pages (~viewport height)
        const normalizeDelta = e => {
            if (e.deltaMode === 1) {
                return e.deltaY * 40;
            }

            if (e.deltaMode === 2) {
                return e.deltaY * window.innerHeight;
            }

            return e.deltaY;
        };

        const handleWheel = e => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                scheduleDelta(normalizeDelta(e));
            }
        };

        let isTracking = false;
        let touchStartX = 0;
        let touchStartY = 0;

        const handleTouchStart = e => {
            if (e.touches.length === 1) {
                isTracking = true;
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
            } else {
                isTracking = false;
            }
        };

        const handleTouchMove = e => {
            if (!isTracking || e.touches.length !== 1) {
                isTracking = false;

                return;
            }

            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;
            const deltaX = Math.abs(touchStartX - currentX);
            const deltaY = Math.abs(touchStartY - currentY);

            if (deltaY >= deltaX) {
                e.preventDefault();
                scheduleDelta(touchStartY - currentY);
            }

            touchStartX = currentX;
            touchStartY = currentY;
        };

        source.addEventListener('wheel', handleWheel, { passive: false });
        source.addEventListener('touchstart', handleTouchStart, { passive: true });
        source.addEventListener('touchmove', handleTouchMove, { passive: false });

        return () => {
            source.removeEventListener('wheel', handleWheel);
            source.removeEventListener('touchstart', handleTouchStart);
            source.removeEventListener('touchmove', handleTouchMove);

            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        };
    }, [sourceRef, targetRef, isEnabled]);
};

export default useScrollForwarding;
